import React from 'react';

// Components
import ContactPortfoliosList from '@/views/components/modals/modal-content/ContactPortfoliosList';
import DisclaimerFooter from '@/views/components/disclaimers/DisclaimerFooter';
import LegacyContactPortfoliosModal from './LegacyContactPortfoliosModal';
import { CloseButton, PrimaryButton, SecondaryButton } from '@/views/components/buttons';
import { ContactsTable } from '@/views/components/tables';
import { SearchInput } from '@/views/components/inputs';
import Modal from '@/views/components/modals/AccessibleModal';
import { Typography } from '@capintel/component-lib/typography';

// Enums
import { ContactsTableFields } from '@/views/components/tables/ContactsTable';
import { GenericEmptyState } from '@/views/components/misc';

// Hooks
import useThemeVersion from '@/hooks/useThemeVersion';

// Libraries
import debounce from 'lodash/debounce';
import union from 'lodash/union';
import { ColumnSort } from '@tanstack/react-table';
import { connect } from 'react-redux';
import { SortingRule } from 'react-table';

// Redux
import { getMyContacts, GET_CONTACTS, setCurrentContact, setPersistenceSource, GET_POST_MESSAGE_CONTACTS } from '@/redux/modules/contacts';
import { ReduxState } from '@/redux/modules/types';
import { selectIsLoadingSome, startLoaderAction } from '@/redux/modules/ui/loaders';
import { setIsActiveContactPostMessage, setIsActivePortfolioPostMessage } from '@/redux/modules/post-message';

// Types
import AppMode from '@/types/app-mode';
import GenericContact from '@/types/generic-contact';
import Portfolio from '@/types/portfolio';
import ThemeMode from '@/types/theme-mode';
import User from '@/types/user';
import UserPreferences from '@/types/user-preferences';
import { ALLOWED_HTML_TAGS } from '../../i18n/FormattedMessageV2';
import { SortingField, SortOrder } from '@/types/data';

// Utils
import heapUtils, { HEAP_CUSTOM_EVENTS } from '@/utils/3rd-party/heap';
import { hasEmptyHoldings } from '@/utils/portfolios';
import { CONTACT_LAYOUT, getContactLayout } from '@/utils/contacts';
import { FormattedMessage, formatMessage } from '@/utils/i18n';
import { PERMISSIONS, userHasPermission } from '@/utils/permissions';
import { getTableSortedColumns } from '@/utils/data-table';
import { SEARCH_DEBOUNCE_TIME } from '@/utils/debounce/constants';
import notification from '@/utils/notification';

type StateProps = {
  user?: User | null;
  contacts: GenericContact[];
  nextContactsToken?: string;
  loading: {
    contacts: boolean;
    portfolios: boolean;
  };
  userPreferences?: UserPreferences;
  isPostMessageActive: boolean;
};

type DispatchProps = {
  getMyContacts: (...args: Parameters<typeof getMyContacts>) => Promise<GenericContact[]>;
  startLoaderAction: (name: string) => void;
  setCurrentContact: (contact: GenericContact) => void;
  setIsActiveContactPostMessage: (isActive: boolean) => void;
  setIsActivePortfolioPostMessage: (isActive: boolean) => void;
  setPersistenceSource: (user: User, key: string, value?: string) => void;
};

type Props = StateProps &
  DispatchProps & {
    query: string;
    show: boolean;
    selectedContact?: {
      contact: GenericContact;
      portfolios?: Portfolio[];
      hasMore: {
        portfolios: boolean;
      };
      sources?: {
        portfolios?: string | undefined;
      };
    };
    isComparing: boolean;
    loadContactPortfolios: (contact: GenericContact) => Promise<void>;
    loadMoreContactPortfolios: (sort?: SortingField[]) => void;
    onSearchContactPortfolios: (query: string, sort?: SortingField[], shouldFetchNext?: boolean) => void;
    onCancel: () => void;
    addPortfoliosToComparison: (portfolios: Portfolio[]) => Promise<void>;
    resetCurrentContact: () => void;
    layout?: CONTACT_LAYOUT;
  };

type State = {
  selectedPortfolios: Portfolio[];
  contactsSearchQuery: string;
  contactsSortingFields: SortingField[];
  contactPortfoliosSortingFields: SortingField[];
  selectedLinkedContact?: GenericContact;
  initialSelectedContact?: GenericContact;
  sortedColumns: ColumnSort[];
};

class ContactPortfoliosModalV2 extends React.Component<Props, State> {
  private isModeContact: boolean = !!(this.props.user && this.props.user.appConfig.mode === AppMode.CONTACT);

  private isThemeLight = this.props.userPreferences?.theme === ThemeMode.LIGHT;

  readonly state: State = {
    selectedPortfolios: [],
    contactsSearchQuery: '',
    contactsSortingFields: [],
    contactPortfoliosSortingFields: [],
    sortedColumns: [],
  };

  debouncedSearchContacts = debounce(() => {
    const { contactsSearchQuery, contactsSortingFields } = this.state;
    this.props.startLoaderAction(GET_CONTACTS.type);
    this.props.getMyContacts({ query: contactsSearchQuery, nextToken: undefined, sort: contactsSortingFields });
  }, SEARCH_DEBOUNCE_TIME);

  componentDidMount() {
    const { selectedContact } = this.props;

    this.setState({ initialSelectedContact: selectedContact?.contact });
    this.props.getMyContacts().then(() => {
      if (this.isModeContact && selectedContact?.contact) {
        this.onSelectContact(selectedContact?.contact);
      }
    });
  }

  clearSearch = () => {
    this.props.onSearchContactPortfolios('');
  };

  clearState = (resetCurrentContact = true) => {
    this.setState({ selectedPortfolios: [] });
    this.clearSearch();
    if (resetCurrentContact || this.isThemeLight) {
      this.props.resetCurrentContact();
    }
  };

  handleBackToContacts = (resetCurrentContact = true) => {
    this.clearState(resetCurrentContact);
    if (this.isModeContact) {
      this.setState({ selectedLinkedContact: undefined });
    }
  };

  handleCancel = () => {
    this.clearState(false);
    this.props.onCancel();

    // For IG we need to make sure the selectedContact stays the same
    if (this.isModeContact && this.state.initialSelectedContact) {
      this.props.setCurrentContact(this.state.initialSelectedContact);
    }
  };

  onSelectPortfolios = (portfolios: Portfolio[]) => {
    const { selectedPortfolios } = this.state;
    const newPortfolios = union(selectedPortfolios, portfolios);
    this.handleSelectionChange(newPortfolios);
  };

  onUnselectPortfolios = (portfolios: Portfolio[]) => {
    const { selectedPortfolios } = this.state;
    const newPortfolios = selectedPortfolios.filter(p => !portfolios.includes(p));
    this.handleSelectionChange(newPortfolios);
  };

  hasEmptyHoldingsInPortfolio = (portfolio: Portfolio): boolean => {
    const { user } = this.props;
    const portfolioHasEmptyHoldings = hasEmptyHoldings(portfolio);

    if (!portfolioHasEmptyHoldings) {
      return false;
    }

    // If user has the permission to allow empty holdings in list, don't disable
    const hasEmptyHoldingsPermission = userHasPermission(user, PERMISSIONS.PORTFOLIOS_ALLOW_EMPTY_HOLDINGS_IN_LIST);
    return !hasEmptyHoldingsPermission;
  };

  handleSelectionChange = (selectedPortfolios: Portfolio[]) => {
    // Check if any of the selected portfolios have empty holdings
    const portfoliosWithEmptyHoldings = selectedPortfolios.filter(this.hasEmptyHoldingsInPortfolio);

    // Check if we previously had any portfolios with empty holdings
    const previouslyHadEmptyHoldings = this.state.selectedPortfolios.some(this.hasEmptyHoldingsInPortfolio);

    // Show notification when transitioning from "no empty holdings" to "has empty holdings"
    if (portfoliosWithEmptyHoldings.length > 0 && !previouslyHadEmptyHoldings) {
      notification.error(formatMessage('PortfolioCard.CompareDisabledEmptyHoldings'));
    }

    this.setState({ selectedPortfolios });
  };

  onComparePortfoliosCapIntel = (selectedPortfolios: Portfolio[]) => {
    this.props.addPortfoliosToComparison(selectedPortfolios).then(() => {
      if (this.isModeContact && this.state.initialSelectedContact) {
        // Reset the current contact for IG
        this.props.setCurrentContact(this.state.initialSelectedContact);
      }
    });
  };

  onComparePortfolios = () => {
    heapUtils.track(HEAP_CUSTOM_EVENTS.NEW_COMPARISON);

    const { selectedPortfolios } = this.state;
    this.onComparePortfoliosCapIntel(selectedPortfolios);
  };

  onSelectContact = (contact: GenericContact) => {
    this.setState({ selectedLinkedContact: contact });
    this.props.loadContactPortfolios(contact);
  };

  onContactsSortingChange = (contactsSortingFields: SortingField[]) => {
    const { contactsSearchQuery } = this.state;

    this.setState({ contactsSortingFields });
    this.props.getMyContacts({ query: contactsSearchQuery, nextToken: undefined, sort: contactsSortingFields });
  };

  onContactsSearchChange = (contactsSearchQuery: string) => {
    this.setState({ contactsSearchQuery });
    this.debouncedSearchContacts();
  };

  onLoadMoreContacts = () => {
    const { contactsSearchQuery, contactsSortingFields } = this.state;
    const { nextContactsToken } = this.props;

    this.props.getMyContacts({ query: contactsSearchQuery, nextToken: nextContactsToken, sort: contactsSortingFields });
  };

  onLoadMorePortfolios = () => {
    const { contactPortfoliosSortingFields } = this.state;

    this.props.loadMoreContactPortfolios(contactPortfoliosSortingFields);
  };

  onContactPortfoliosSearchChange = (query: string) => {
    const { contactPortfoliosSortingFields } = this.state;
    this.props.onSearchContactPortfolios(query, contactPortfoliosSortingFields, false);
  };

  onContactPortfoliosSortingChange = (sortingRules: SortingRule[]) => {
    const { query } = this.props;
    const contactPortfoliosSortingFields = sortingRules.map(rule => ({
      id: rule.id,
      order: rule.desc ? SortOrder.Desc : SortOrder.Asc,
    }));

    this.setState({ contactPortfoliosSortingFields });
    this.props.onSearchContactPortfolios(query, contactPortfoliosSortingFields, false);
  };

  selectedPortfoliosHaveEmptyHoldings = () => {
    const { selectedPortfolios } = this.state;
    return selectedPortfolios.some(this.hasEmptyHoldingsInPortfolio);
  };

  renderEmptyMessage = () => {
    const { layout } = this.props;
    const emptyStateHeader =
      layout === CONTACT_LAYOUT.CLIENT
        ? formatMessage('EmptyState.NoContactPortfolios.ClientTitle')
        : formatMessage('EmptyState.NoContactPortfolios.ContactTitle');

    return <GenericEmptyState header={emptyStateHeader} />;
  };

  renderNoResultsMessage = () => (
    <GenericEmptyState
      header={formatMessage('EmptyState.NoResults.Title')}
      body={formatMessage('EmptyState.NoResults.Text')}
      actionLabel={formatMessage('EmptyState.NoResults.ClearSearch')}
      onAction={() => this.props.onSearchContactPortfolios('')}
    />
  );

  renderNoResults = () => {
    const { query } = this.props;

    if (query) {
      return this.renderNoResultsMessage();
    }

    return this.renderEmptyMessage();
  };

  getTableFields = () => {
    const defaultFields = [ContactsTableFields.Email, ContactsTableFields.Category, ContactsTableFields.Id];
    const orderedFields = !this.isThemeLight
      ? [ContactsTableFields.Uid, ContactsTableFields.Fname, ContactsTableFields.Lname]
      : [ContactsTableFields.Fname, ContactsTableFields.Lname, ContactsTableFields.Uid];
    return [...orderedFields, ...defaultFields];
  };

  handleContactsSorting = (sort: ColumnSort) => {
    const { query } = this.props;
    const { serverSortingParams, columnSort } = getTableSortedColumns(this.state.contactsSortingFields, sort);
    this.setState({ contactsSortingFields: serverSortingParams, sortedColumns: columnSort });
    this.props.getMyContacts({ query, nextToken: undefined, sort: serverSortingParams });
  };

  handlePortfoliosSorting = (sort: ColumnSort) => {
    const { query } = this.props;
    const { serverSortingParams, columnSort } = getTableSortedColumns(this.state.contactPortfoliosSortingFields, sort);
    this.setState({ contactPortfoliosSortingFields: serverSortingParams, sortedColumns: columnSort });
    this.props.onSearchContactPortfolios(query, serverSortingParams, false);
  };

  render() {
    const {
      contacts,
      show,
      user,
      selectedContact,
      query,
      layout,
      isComparing,
      nextContactsToken,
      loading,
      isPostMessageActive,
      setIsActiveContactPostMessage,
      setIsActivePortfolioPostMessage,
      setPersistenceSource,
    } = this.props;

    const { selectedPortfolios, contactsSearchQuery, selectedLinkedContact } = this.state;

    if (!user) {
      return null;
    }

    const title =
      layout === CONTACT_LAYOUT.CLIENT ? formatMessage('ContactPortfoliosModal.SelectClient') : formatMessage('ContactPortfoliosModal.SelectContact');

    const backButtonText = layout === CONTACT_LAYOUT.CLIENT ? 'ContactPortfoliosModal.BackToClients' : 'ContactPortfoliosModal.BackToContacts';

    const emptyStateHeader =
      layout === CONTACT_LAYOUT.CLIENT
        ? formatMessage('EmptyState.NoContactPortfolios.ClientTitle')
        : formatMessage('EmptyState.NoContactPortfolios.ContactTitle');

    const isContacts = !selectedContact || (this.isModeContact && selectedContact && !selectedLinkedContact);
    const isContactDetails = (!this.isModeContact && selectedContact) || (this.isModeContact && selectedContact && selectedLinkedContact);

    const renderContactSearchInput = (): JSX.Element | null => {
      const { user } = this.props;
      const { contactsSearchQuery } = this.state;
      const layout = getContactLayout(user);
      const searchPlaceholder =
        layout === CONTACT_LAYOUT.CLIENT ? formatMessage('Contacts.Search.PlaceholderClient') : formatMessage('Contacts.Search.Placeholder');

      if (!this.props.contacts.length && !this.state.contactsSearchQuery) return null;

      return (
        <div className="modal-search-input">
          <SearchInput
            className="contacts-search-input search-input-v2"
            onChange={this.onContactsSearchChange}
            placeholder={searchPlaceholder}
            value={contactsSearchQuery}
            data-private
            data-dd-privacy="mask"
          />
        </div>
      );
    };

    const renderContactPortfoliosSearchInput = (): JSX.Element => (
      <div className="modal-search-input">
        <SearchInput
          className="search-input-v2"
          placeholder={formatMessage('ContactPortfoliosModal.SearchPortfolios')}
          onChange={this.onContactPortfoliosSearchChange}
          value={query}
          data-private
          data-dd-privacy="mask"
        />
      </div>
    );

    const renderContactsTable = (): JSX.Element | null => {
      if (!isContacts) return null;

      return (
        <>
          {renderContactSearchInput()}
          <div className="modal-table">
            <ContactsTable
              selectable={false}
              className="flex-fill"
              contacts={contacts}
              title={title}
              onClick={this.onSelectContact}
              user={user}
              layout={layout}
              onSortingChange={this.onContactsSortingChange}
              onLoadMore={this.onLoadMoreContacts}
              hasMore={nextContactsToken != null}
              onContactsSearch={this.onContactsSearchChange}
              debouncedSearchContacts={this.debouncedSearchContacts}
              searchQuery={contactsSearchQuery}
              loading={loading.contacts}
              isThemeLight={this.isThemeLight}
              orderedFields={this.getTableFields()}
              serverSideSorting
              onServerSideSorting={this.handleContactsSorting}
              sortedColumns={this.state.sortedColumns}
              isPostMessageActive={isPostMessageActive}
              setPersistenceSource={setPersistenceSource}
              setIsActiveContactPostMessage={setIsActiveContactPostMessage}
            />
          </div>
        </>
      );
    };

    const renderContactPortfoliosTable = (): JSX.Element | null => {
      if (!isContactDetails) return null;

      return (
        <>
          {renderContactPortfoliosSearchInput()}
          <div className="modal-table">
            <ContactPortfoliosList
              user={user}
              backButtonText={backButtonText}
              emptyStateHeader={emptyStateHeader}
              isLoading={loading.portfolios}
              searchQuery={query}
              selectedContact={selectedContact}
              onBack={this.handleBackToContacts}
              onLoadMorePortfolios={this.onLoadMorePortfolios}
              onSearchPortfolios={this.onContactPortfoliosSearchChange}
              onSelectPortfolios={this.onSelectPortfolios}
              onSortPortfolios={this.onContactPortfoliosSortingChange}
              onUnselectPortfolios={this.onUnselectPortfolios}
              isThemeLight={this.isThemeLight}
              setPersistenceSource={setPersistenceSource}
              onRowSelectionChange={this.handleSelectionChange}
              serverSideSorting
              onServerSideSorting={this.handlePortfoliosSorting}
              sortedColumns={this.state.sortedColumns}
              isPostMessageActive={isPostMessageActive}
              setIsActivePortfolioPostMessage={setIsActivePortfolioPostMessage}
            />
          </div>
          {!loading.portfolios && selectedContact.portfolios && selectedContact.portfolios.length > 0 && (
            <DisclaimerFooter disclaimers={[formatMessage('Disclaimers.NET_PERFORMANCE')]} />
          )}
        </>
      );
    };

    const renderTable = (): JSX.Element => (
      <div className="modal-table-container">
        {renderContactsTable()}
        {renderContactPortfoliosTable()}
      </div>
    );

    // MODAL HEADER
    const renderHeader = (): JSX.Element => {
      const { selectedContact } = this.props;
      const contactFirstName = selectedContact?.contact.fname;
      const contactLastName = selectedContact?.contact.lname;
      const contactFormattedName = userHasPermission(user, PERMISSIONS.CONTACTS_LAST_NAME) ? `${contactFirstName} ${contactLastName}` : contactFirstName;

      const modalTitle =
        selectedContact && contactFormattedName ? (
          <FormattedMessage id="ContactPortfoliosModal.ContactTitle" values={{ name: contactFormattedName }} data-private data-dd-privacy="mask" />
        ) : (
          title
        );

      return (
        <Modal.Header>
          <div className="modal-title">
            <Typography as={ALLOWED_HTML_TAGS.H2} variant="headerXs">
              {modalTitle}
            </Typography>
          </div>
          <CloseButton onClick={this.handleCancel} />
        </Modal.Header>
      );
    };

    // MODAL BODY
    const renderBody = (): JSX.Element => <Modal.Body>{renderTable()}</Modal.Body>;

    // MODAL FOOTER
    const renderFooter = (): JSX.Element => {
      const primaryButtonId = `ContactPortfoliosModal.${isComparing ? 'ComparingSelected' : 'CompareSelection'}`;
      if (isContacts) {
        return <></>;
      }

      const hasEmptyHoldings = this.selectedPortfoliosHaveEmptyHoldings();
      const isDisabled = !selectedPortfolios.length || isComparing || hasEmptyHoldings;

      return (
        <Modal.Footer>
          <SecondaryButton
            className="secondary-v2"
            onClick={() => this.handleBackToContacts()}
            data-testid="contactPortfoliosModal-secondaryButton-backToContacts"
            disabled={isComparing}
          >
            {formatMessage(backButtonText)}
          </SecondaryButton>

          <PrimaryButton
            loading={isComparing}
            disabled={isDisabled}
            className="primary-v2"
            data-testid="contactPortfoliosModal-primaryButton-compare"
            onClick={this.onComparePortfolios}
          >
            {formatMessage(primaryButtonId, { count: selectedPortfolios.length })}
          </PrimaryButton>
        </Modal.Footer>
      );
    };

    return (
      <Modal backdropClassName="modal-v2-backdrop" className="modal-v2 contact-portfolios-modal-v2" show={show} onHide={this.handleCancel}>
        {renderHeader()}
        {renderBody()}
        {renderFooter()}
      </Modal>
    );
  }
}

const ContactPortfoliosModal = (props: Props) => {
  const { isLightVersion } = useThemeVersion();
  if (isLightVersion) {
    return <ContactPortfoliosModalV2 {...props} />;
  }
  return <LegacyContactPortfoliosModal {...props} />;
};

const mapStateToProps = (state: ReduxState): StateProps => ({
  user: state.auth.user,
  contacts: state.contacts.contacts,
  nextContactsToken: state.contacts.nextContactsToken,
  loading: {
    contacts: selectIsLoadingSome(state, [GET_CONTACTS.type, GET_POST_MESSAGE_CONTACTS.type]),
    portfolios: state.contacts.loading.current.portfolios,
  },
  userPreferences: state.auth.user?.preferences,
  isPostMessageActive: state.postMessage.isPostMessageActive,
});

export default connect(mapStateToProps, {
  getMyContacts,
  startLoaderAction,
  setCurrentContact,
  setIsActiveContactPostMessage,
  setIsActivePortfolioPostMessage,
  setPersistenceSource,
})(ContactPortfoliosModal);
